import { useState } from 'react';

import {
  IconBuilding,
  IconShieldCheck,
  IconBarChartAxisUp,
  IconStarOutline,
  IconArrowRight,
} from '@iwoca/icons';
import { Link } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';

import styles from './QuestionScreen.module.css';
import { useLocale } from '../../../hooks/useLocale';
import { Button } from '../../Button/Button';
import { ChoiceCards } from '../components/ChoiceCards';
import { ExitToSignupLink } from '../components/ExitToSignupLink';
import { NumberField } from '../components/NumberField';
import { ProgressBar } from '../components/ProgressBar';
import { TQuestionDef, TAnswerValue } from '../logic/types';

const QUESTION_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  building: IconBuilding,
  shield: IconShieldCheck,
  chart: IconBarChartAxisUp,
  star: IconStarOutline,
};

type TProps = {
  question: TQuestionDef;
  index: number;
  total: number;
  value: TAnswerValue;
  onAnswer: (value: TAnswerValue) => void;
  onNext: () => void;
  onSkip: () => void;
  onBack: () => void;
};

export const QuestionScreen = ({
  question,
  index,
  total,
  value,
  onAnswer,
  onNext,
  onSkip,
  onBack,
}: TProps) => {
  const { t } = useLingui();
  const { i18n } = useLocale();
  const [rangeError, setRangeError] = useState<string | undefined>(undefined);

  const isLastQuestion = index === total;
  const QuestionIcon = QUESTION_ICONS[question.icon];
  const title = i18n._(question.title);

  const isOutOfRange = (candidate: TAnswerValue) => {
    if (question.kind !== 'number' || typeof candidate !== 'number') {
      return false;
    }
    const min = question.min ?? -Infinity;
    const max = question.max ?? Infinity;
    return candidate < min || candidate > max;
  };

  const handleNext = () => {
    if (isOutOfRange(value)) {
      setRangeError(
        question.rangeError ? i18n._(question.rangeError) : undefined,
      );
      return;
    }
    setRangeError(undefined);
    onNext();
  };

  const handleBlur = () => {
    if (isOutOfRange(value)) {
      setRangeError(
        question.rangeError ? i18n._(question.rangeError) : undefined,
      );
    }
  };

  const handleAnswer = (newValue: TAnswerValue) => {
    setRangeError(undefined);
    onAnswer(newValue);
  };

  const forwardLabel = isLastQuestion ? t`View assessment` : t`Next`;

  return (
    <div className={styles.container}>
      <ProgressBar index={index} total={total} onBack={onBack} />

      <div className={styles.questionHead}>
        <div className={styles.eyebrowRow}>
          {QuestionIcon && (
            <span className={styles.iconBadge}>
              <QuestionIcon className={styles.questionIcon} />
            </span>
          )}
          <p className={styles.eyebrow}>{i18n._(question.eyebrow)}</p>
        </div>
        <h2 className={styles.title}>{title}</h2>
        {question.help && (
          <p className={styles.help}>{i18n._(question.help)}</p>
        )}
      </div>

      <div
        className={
          question.kind === 'number' && rangeError
            ? `${styles.input} ${styles.inputNumber}`
            : styles.input
        }
      >
        {question.kind === 'choice' && question.choices && (
          <ChoiceCards
            choices={question.choices}
            value={typeof value === 'string' ? value : undefined}
            onSelect={handleAnswer}
            groupLabel={title}
          />
        )}
        {question.kind === 'number' && (
          <NumberField
            value={typeof value === 'number' ? value : undefined}
            onChange={handleAnswer}
            onBlur={handleBlur}
            placeholder={
              question.placeholder ? i18n._(question.placeholder) : undefined
            }
            suffix={question.suffix ? i18n._(question.suffix) : undefined}
            error={rangeError}
            ariaLabel={title}
          />
        )}
      </div>

      {question.info && (
        <div className={styles.info}>
          <p className={styles.infoText}>{i18n._(question.info.text)}</p>
          <Link
            href={question.info.href}
            size="small"
            target="_blank"
            rel="noreferrer"
          >
            {i18n._(question.info.link)}
          </Link>
        </div>
      )}

      <div className={styles.nav}>
        <Button
          variant="primary"
          onClick={handleNext}
          aria-label={forwardLabel}
          className={styles.nextButton}
          type="button"
        >
          {forwardLabel}
          <IconArrowRight />
        </Button>
      </div>

      <Button
        variant="tertiary"
        size="small"
        onClick={onSkip}
        className={styles.skipButton}
        type="button"
      >
        <Trans>Skip this question</Trans>
      </Button>

      <ExitToSignupLink phase="question" questionIndex={index} />
    </div>
  );
};
